import { DateTime } from 'luxon';

// Luxon weekday numbering: Monday = 1 … Sunday = 7.
const WEEKDAY_NUMBERS: Record<string, number> = {
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
    sunday: 7,
};

const INTERVAL_UNITS: Record<string, string> = {
    hourly: 'hours',
    daily: 'days',
    monthly: 'months',
    yearly: 'years',
};

type Recurrence = {
    repeat: string;
    interval?: number | string;
    weekdays?: string[];
    stopCondition?: string;
    count?: number | string;
    until?: string | DateTime;
};

// Weekly cycles are anchored to the start date and repeat every `interval`
// weeks; within each cycle every selected weekday resolves to the next-or-same
// matching day. For a past start we jump to the cycle covering today (mirroring
// WeeklySchedule), so the dates returned are the upcoming ones, not historical.
function upcomingWeekly(start: DateTime, weekdays: string[], interval: number, today: DateTime, count: number): DateTime[] {
    const targets = weekdays.map((day) => WEEKDAY_NUMBERS[day]).filter(Boolean);
    if (!targets.length) {
        return [];
    }

    const startDay = start.startOf('day');
    let cycleStart = startDay;
    if (today > startDay) {
        const weeksSinceStart = Math.floor(today.diff(startDay, 'weeks').weeks);
        const cyclesPassed = Math.floor(weeksSinceStart / interval);
        cycleStart = startDay.plus({ weeks: cyclesPassed * interval });
    }

    const occurrences: DateTime[] = [];
    const seen = new Set<string>();

    for (let cycle = 0; occurrences.length < count && cycle < count + 2; cycle++) {
        const cycleDates = targets
            .map((weekday) => {
                const offset = (weekday - cycleStart.weekday + 7) % 7;
                return cycleStart.plus({ days: offset }).set({ hour: start.hour, minute: start.minute });
            })
            .sort((a, b) => a.toMillis() - b.toMillis());

        for (const date of cycleDates) {
            const key = date.toISODate() ?? '';
            if (date.startOf('day') >= today && !seen.has(key)) {
                seen.add(key);
                occurrences.push(date);
            }
        }

        cycleStart = cycleStart.plus({ weeks: interval });
    }

    return occurrences.slice(0, count);
}

// Interval cadences (hourly/daily/monthly/yearly) step from the start by a fixed
// number of units. Return the bucket containing now (start ≤ now < start +
// interval) and the following ones — matching IntervalBasedSchedule's
// full-timestamp anchoring, so sub-day units like hourly land on the current
// slot rather than a time earlier today.
function upcomingInterval(start: DateTime, unit: string, interval: number, now: DateTime, count: number): DateTime[] {
    let candidate = start;

    if (candidate < now) {
        // Jump to roughly now (calendar-aware), undershooting by one interval so
        // the forward walk below always lands on the bucket containing now. This
        // avoids iterating from a long-past start (which previously hit a loop
        // guard and truncated for hourly schedules).
        const elapsed = now.diff(candidate, unit).as(unit);
        const jumps = Math.max(0, Math.floor(elapsed / interval) - 1);
        if (jumps > 0) {
            candidate = candidate.plus({ [unit]: interval * jumps });
        }
        let guard = 0;
        while (candidate.plus({ [unit]: interval }) <= now && guard++ < 64) {
            candidate = candidate.plus({ [unit]: interval });
        }
    }

    const occurrences: DateTime[] = [];
    for (let i = 0; i < count; i++) {
        occurrences.push(candidate);
        candidate = candidate.plus({ [unit]: interval });
    }

    return occurrences;
}

// The next `count` occurrence start-times on or after today, mirroring the
// backend WeeklySchedule / IntervalBasedSchedule cycle anchoring so a past start
// surfaces the upcoming tasks rather than historical ones.
export function upcomingOccurrences(start: DateTime | null | undefined, recurrence: Recurrence, now: DateTime, count = 3): DateTime[] {
    if (!start?.isValid || count < 1) {
        return [];
    }

    // Whole-week/period increments only — guard against a fractional interval.
    const interval = Math.max(1, Math.floor(Number(recurrence.interval)) || 1);

    let occurrences: DateTime[];
    if (recurrence.repeat === 'weekly') {
        occurrences = upcomingWeekly(start, recurrence.weekdays ?? [], interval, now.startOf('day'), count);
    } else {
        const unit = INTERVAL_UNITS[recurrence.repeat];
        if (!unit) {
            return [];
        }
        occurrences = upcomingInterval(start, unit, interval, now, count);

        // A "stop after N occurrences" series produces exactly N occurrences
        // total — indices 0…N-1 stepping from the start — so drop any projected
        // bucket whose absolute index has already run past the count. This needs
        // no created-task data: the cadence grid is deterministic. (Weekly count
        // limits aren't capped here — that requires mirroring the backend's
        // per-weekday occurrence counting.)
        if (recurrence.stopCondition === 'count') {
            const limit = Math.floor(Number(recurrence.count));
            if (Number.isFinite(limit) && limit >= 0) {
                occurrences = occurrences.filter((occurrence) => Math.round(occurrence.diff(start, unit).as(unit) / interval) < limit);
            }
        }
    }

    // A series that ends on a date produces nothing past it.
    if (recurrence.stopCondition === 'until' && recurrence.until) {
        const until = DateTime.isDateTime(recurrence.until) ? recurrence.until : DateTime.fromISO(recurrence.until);
        if (until.isValid) {
            const cap = until.endOf('day');
            return occurrences.filter((occurrence) => occurrence <= cap);
        }
    }

    return occurrences;
}
